import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, Image, TouchableOpacity } from 'react-native';
import { Card, Title, Text, Button, Divider, ActivityIndicator, List, Chip, useTheme, Avatar } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { ErrorMessage } from '../../components';
import { getActivityById, updateActivityStatus, deleteActivity } from '../../api';
import { formatDateTime, formatDate, getMinutesBetweenDates } from '../../utils';
import { showAlert } from '../../navigation';

const ActivityDetailScreen = ({ navigation, route }) => {
  const theme = useTheme();
  const { activityId, childId } = route.params;
  
  // State variables
  const [activity, setActivity] = useState(null);
  const [childActivities, setChildActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Örnek çocuk verisi
  const childData = {
    id: childId,
    name: childId === '1' ? 'Ayşe' : childId === '2' ? 'Mehmet' : 'Zeynep',
    age: childId === '1' ? 7 : childId === '2' ? 9 : 5,
    image: null
  };
  
  // Fetch activity data
  useEffect(() => {
    fetchActivityData();
  }, [activityId]);
  
  // Fetch activity data
  const fetchActivityData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getActivityById(activityId);
      setActivity(response.activity);
      
      // Çocuğun tüm aktivitelerini getir
      const activitiesOfChild = response.activities.filter(a => a.childId === childId);
      setChildActivities(activitiesOfChild);
      
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setError(err.message || 'Aktivite bilgileri yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update activity status
  const handleUpdateStatus = async (newStatus) => {
    Alert.alert(
      'Durum Güncelle',
      `Bu aktiviteyi ${newStatus === 'completed' ? 'tamamlandı' : 'iptal edildi'} olarak işaretlemek istediğinize emin misiniz?`,
      [
        { text: 'İptal', style: 'cancel' },
        {
          text: 'Evet, Güncelle',
          onPress: async () => {
            setIsUpdating(true);
            try {
              await updateActivityStatus(activityId, newStatus);
              // Aktiviteyi güncelle
              fetchActivityData();
              showAlert('Başarılı', 'Aktivite durumu güncellendi.');
            } catch (err) {
              console.error('Error updating activity status:', err);
              showAlert('Hata', err.message || 'Aktivite durumu güncellenirken bir hata oluştu.');
            } finally {
              setIsUpdating(false);
            }
          },
        },
      ]
    );
  };
  
  // Delete activity
  const handleDeleteActivity = () => {
    Alert.alert(
      'Aktiviteyi Sil',
      'Bu aktiviteyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
      [
        { text: 'İptal', style: 'cancel' },
        {
          text: 'Sil',
          style: 'destructive',
          onPress: async () => {
            setIsUpdating(true);
            try {
              await deleteActivity(activityId);
              showAlert('Başarılı', 'Aktivite başarıyla silindi.');
              // Aktiviteler ekranına geri dön
              navigation.goBack();
            } catch (err) {
              console.error('Error deleting activity:', err);
              showAlert('Hata', err.message || 'Aktivite silinirken bir hata oluştu.');
              setIsUpdating(false);
            }
          },
        },
      ]
    );
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return { backgroundColor: theme.colors.primary, color: 'white' };
      case 'completed':
        return { backgroundColor: '#4CAF50', color: 'white' };
      case 'upcoming':
        return { backgroundColor: '#FF9800', color: 'black' };
      case 'cancelled':
        return { backgroundColor: '#e74c3c', color: 'white' };
      default:
        return { backgroundColor: '#f1c40f', color: 'black' };
    }
  };
  
  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Devam Ediyor';
      case 'completed':
        return 'Tamamlandı';
      case 'upcoming':
        return 'Yaklaşan';
      case 'cancelled':
        return 'İptal Edildi';
      default:
        return 'Bilinmiyor';
    }
  };
  
  // Calculate activity duration
  const getActivityDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Belirtilmemiş';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const minutes = Math.round((end - start) / (1000 * 60));
    
    if (minutes < 60) {
      return `${minutes} dakika`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (remainingMinutes === 0) {
        return `${hours} saat`;
      } else {
        return `${hours} saat ${remainingMinutes} dakika`;
      }
    }
  };
  
  // Render child activity item
  const renderChildActivityItem = (item) => {
    return (
      <TouchableOpacity
        key={item.id}
        style={styles.historyItem}
        onPress={() => 
          navigation.navigate('ActivityDetail', { 
            activityId: item.id,
            childId: childId
          })
        }
      >
        <Image 
          source={{ uri: item.imageUrl }} 
          style={styles.historyItemImage}
          resizeMode="cover"
        />
        
        <View style={styles.historyItemContent}>
          <View style={styles.historyItemHeader}>
            <Text style={styles.historyItemTitle} numberOfLines={1}>{item.title}</Text>
            <Chip 
              style={[
                styles.historyStatusChip, 
                {backgroundColor: getStatusColor(item.status).backgroundColor}
              ]}
              textStyle={{ color: getStatusColor(item.status).color, fontSize: 10 }}
            >
              {getStatusText(item.status)}
            </Chip>
          </View>
          
          <View style={styles.historyItemDetails}>
            <View style={styles.historyDetailItem}>
              <MaterialCommunityIcons name="calendar" size={14} color="#666" />
              <Text style={styles.historyDetailText}>{item.date}</Text>
            </View>
            
            <View style={styles.historyDetailItem}>
              <MaterialCommunityIcons name="tag" size={14} color="#666" />
              <Text style={styles.historyDetailText}>{item.category}</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };
  
  // Loading state
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Aktivite bilgileri yükleniyor...</Text>
      </View>
    );
  }
  
  // Error state
  if (error) {
    return (
      <View style={styles.container}>
        <ErrorMessage error={error} onRetry={fetchActivityData} />
      </View>
    );
  }
  
  // Not found state
  if (!activity) {
    return (
      <View style={styles.container}>
        <ErrorMessage 
          error="Aktivite bulunamadı" 
          onRetry={() => navigation.goBack()} 
          retryButtonText="Geri Dön"
        />
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      <ScrollView>
        {/* Çocuk Bilgileri */}
        <Card style={styles.childCard}>
          <Card.Content style={styles.childCardContent}>
            <Avatar.Text
              size={50}
              label={childData.name.substring(0, 2)}
              style={{ backgroundColor: theme.colors.primary }}
            />
            <View style={styles.childInfo}>
              <Title style={styles.childName}>{childData.name}</Title>
              <Text style={styles.childAge}>{childData.age} yaşında</Text>
            </View>
          </Card.Content>
        </Card>
        
        {/* Aktivite Detay Kartı */}
        <Card style={styles.activityCard}>
          <Card.Content>
            {activity.imageUrl && (
              <Image 
                source={{ uri: activity.imageUrl }} 
                style={styles.activityImage}
                resizeMode="cover"
              />
            )}
            
            <View style={styles.headerContainer}>
              <Title style={styles.titleText}>{activity.title}</Title>
              <Chip 
                style={[styles.statusChip, { backgroundColor: getStatusColor(activity.status).backgroundColor }]}
                textStyle={{ color: getStatusColor(activity.status).color }}
              >
                {getStatusText(activity.status)}
              </Chip>
            </View>
            
            <List.Section>
              <List.Item
                title="Kategori"
                description={activity.category}
                left={props => <List.Icon {...props} icon="tag" />}
              />
              
              <List.Item
                title="Tarih"
                description={activity.date}
                left={props => <List.Icon {...props} icon="calendar" />}
              />
              
              <List.Item
                title="Saat"
                description={activity.time}
                left={props => <List.Icon {...props} icon="clock-outline" />}
              />
              
              <List.Item
                title="Süre"
                description={getActivityDuration(activity.startTime, activity.endTime)}
                left={props => <List.Icon {...props} icon="timer" />}
              />
              
              <List.Item
                title="Eğitmen"
                description={activity.teacherName}
                left={props => <List.Icon {...props} icon="account" />}
              />
              
              <List.Item
                title="Ücret"
                description={`${activity.price} TL`}
                left={props => <List.Icon {...props} icon="currency-try" />}
              />
              
              <List.Item
                title="Konum"
                description={activity.location}
                left={props => <List.Icon {...props} icon="map-marker" />}
              />
              
              <List.Item
                title="Açıklama"
                description={activity.description}
                left={props => <List.Icon {...props} icon="information" />}
              />
            </List.Section>
            
            {activity.status === 'active' && (
              <View style={styles.actionsContainer}>
                <Button 
                  mode="contained" 
                  icon="check-circle" 
                  onPress={() => handleUpdateStatus('completed')}
                  style={[styles.actionButton, { backgroundColor: '#2ecc71' }]}
                  loading={isUpdating}
                  disabled={isUpdating}
                >
                  Tamamlandı
                </Button>
                <Button 
                  mode="contained" 
                  icon="cancel" 
                  onPress={() => handleUpdateStatus('cancelled')}
                  style={[styles.actionButton, { backgroundColor: '#e74c3c' }]}
                  loading={isUpdating}
                  disabled={isUpdating}
                >
                  İptal Et
                </Button>
              </View>
            )}
            
            <Divider style={styles.divider} />
            
            <View style={styles.footerButtons}>
              <Button 
                mode="outlined" 
                icon="pencil" 
                onPress={() => navigation.navigate('CreateActivity', { activityId: activity.id })}
                style={styles.footerButton}
                disabled={isUpdating || activity.status !== 'active'}
              >
                Düzenle
              </Button>
              <Button 
                mode="outlined" 
                icon="delete" 
                onPress={handleDeleteActivity}
                style={[styles.footerButton, styles.deleteButton]}
                color="#e74c3c"
                loading={isUpdating}
                disabled={isUpdating}
              >
                Sil
              </Button>
            </View>
          </Card.Content>
        </Card>
        
        {/* Aktivite Geçmişi */}
        <Card style={styles.historyCard}>
          <Card.Content>
            <Title style={styles.historyTitle}>{childData.name}'nin Aktivite Geçmişi</Title>
            
            {childActivities.length > 0 ? (
              <View style={styles.historyList}>
                {childActivities.map(act => renderChildActivityItem(act))}
              </View>
            ) : (
              <View style={styles.emptyHistory}>
                <MaterialCommunityIcons name="calendar-alert" size={40} color="#ccc" />
                <Text style={styles.emptyHistoryText}>Henüz hiçbir aktiviteye katılmamış</Text>
              </View>
            )}
          </Card.Content>
        </Card>
      </ScrollView>
      
      <View style={styles.bottomButtons}>
        <Button 
          mode="contained" 
          icon="arrow-left" 
          onPress={() => navigation.goBack()}
          style={[styles.bottomButton, { backgroundColor: theme.colors.primary }]}
        >
          Aktivitelere Dön
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#555',
  },
  childCard: {
    margin: 16,
    marginBottom: 8,
    elevation: 2,
  },
  childCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  childInfo: {
    marginLeft: 16,
  },
  childName: {
    fontSize: 18,
    marginBottom: 4,
  },
  childAge: {
    fontSize: 14,
    color: '#666',
  },
  activityCard: {
    margin: 16,
    marginTop: 8,
    marginBottom: 8,
    elevation: 2,
  },
  activityImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
    marginBottom: 16,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  titleText: {
    fontSize: 20,
    flex: 1,
  },
  statusChip: {
    height: 32,
  },
  historyCard: {
    margin: 16,
    marginTop: 8,
    paddingBottom: 8,
    elevation: 2,
  },
  historyTitle: {
    fontSize: 18,
    marginBottom: 16,
  },
  historyList: {
    marginTop: 8,
  },
  historyItem: {
    flexDirection: 'row',
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
    elevation: 1,
  },
  historyItemImage: {
    width: 80,
    height: 80,
  },
  historyItemContent: {
    flex: 1,
    padding: 10,
  },
  historyItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  historyItemTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    flex: 1,
  },
  historyStatusChip: {
    height: 24,
    paddingHorizontal: 4,
  },
  historyItemDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  historyDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 4,
  },
  historyDetailText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#666',
  },
  emptyHistory: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  emptyHistoryText: {
    marginTop: 8,
    color: '#666',
    textAlign: 'center',
  },
  bottomButtons: {
    flexDirection: 'row',
    padding: 16,
    backgroundColor: 'white',
    elevation: 4,
  },
  bottomButton: {
    flex: 1,
  },
  divider: {
    marginVertical: 16,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  actionButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  footerButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  deleteButton: {
    borderColor: '#e74c3c',
  },
});

export default ActivityDetailScreen;
